import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';

import { BackHeader, PageTemplate } from '@_widgets/common';

import { useFolderListQuery } from '@_features/folder';

import { folderApi, TFolder } from '@_entities/content';

import { HamburgerMenuIcon } from '@_shared/icons';
import { predicateType } from '@_shared/libs';
import { Button } from '@_shared/ui/Button';
import { Typography } from '@_shared/ui/Typography';

import * as S from './FolderListManualPage.style';

export function FolderListManualPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: initializeFolderList, isLoading } = useFolderListQuery('manual');
	const [folderList, setFolderList] = useState<TFolder[]>(initializeFolderList ?? []);

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const newItems = Array.from(folderList);
		const [removed] = newItems.splice(result.source.index, 1);
		newItems.splice(result.destination.index, 0, removed);

		setFolderList(newItems);
	};

	useEffect(() => {
		if (!isLoading) {
			setFolderList(initializeFolderList ?? []);
		}
	}, [initializeFolderList, isLoading]);

	function isSubmitButtonEnabled() {
		const initialFlatFolderList = initializeFolderList?.map((folder) => folder.id);
		const currentFlatFolderList = folderList.map((folder) => folder.id);

		return !initialFlatFolderList?.every((id, index) => id === currentFlatFolderList?.[index]);
	}

	return (
		<PageTemplate
			header={
				<BackHeader>
					<Typography type="title-large" color="font-secondary">
						사용자 지정 정렬
					</Typography>
				</BackHeader>
			}
			nav={
				<S.BottomButtonContainer>
					<Button
						bgColor="warmgray-300"
						fullWidth
						height="50px"
						onClick={() => {
							navigate(-1);
						}}
					>
						<Typography type="title-large" color="white-primary">
							취소
						</Typography>
					</Button>
					<Button
						bgColor={isSubmitButtonEnabled() ? 'orange-primary' : 'warmgray-300'}
						onClick={() => {
							if (isSubmitButtonEnabled()) {
								const flatFolderIds = folderList.map((folder) => folder.id);
								folderApi.updateFolderListOrder(flatFolderIds).then(() => {
									window.location.reload();
									queryClient.invalidateQueries({
										predicate: (query) => predicateType(query, 'folderList'),
									});
								});
							}
						}}
						fullWidth
						height="50px"
					>
						<Typography type="title-large" color="white-primary">
							순서변경하기
						</Typography>
					</Button>
				</S.BottomButtonContainer>
			}
		>
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="droppable">
					{(droppableProvided) => (
						<div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
							{folderList.map((folder, index) => (
								<Draggable
									key={folder.id}
									draggableId={folder.id.toString()}
									index={index}
								>
									{(draggableProvided, draggableSnapshot) => (
										<div
											ref={draggableProvided.innerRef}
											{...draggableProvided.draggableProps}
										>
											<S.FolderItem isDragging={draggableSnapshot.isDragging}>
												<S.FolderInfo
													isDragging={draggableSnapshot.isDragging}
												>
													<Typography
														type="title-medium"
														color="font-primary"
													>
														{folder.name}
													</Typography>
													<Typography
														type="label-medium"
														color="font-tertiary"
													>
														{folder.count}개
													</Typography>
												</S.FolderInfo>
												<S.DragHandle
													{...draggableProvided.dragHandleProps}
												>
													<HamburgerMenuIcon />
												</S.DragHandle>
											</S.FolderItem>
										</div>
									)}
								</Draggable>
							))}
							{droppableProvided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</PageTemplate>
	);
}
